/**
 * Image Renderer Utility
 * Exports the pole measurement photo rendering logic for use in external systems
 */

import type {
  PoleImageType,
  PoleAttachmentType,
  GuyAnchorType,
  PoleFormatOption,
} from '../types';

export interface RenderMeasurementPhotoOptions {
  imageUrl: string;
  poleImage: PoleImageType;
  attachments: PoleAttachmentType[];
  guyAnchors?: GuyAnchorType[];
  measurementUnit?: PoleFormatOption;
  quality?: number;
}

/**
 * Renders a measurement photo with annotations using the official pole-canvas logic
 * This is the same rendering logic used in the PoleCanvas component
 */
export async function renderMeasurementPhoto({
  imageUrl,
  poleImage,
  attachments,
  guyAnchors = [],
  measurementUnit = 'metric',
  quality = 0.9
}: RenderMeasurementPhotoOptions): Promise<string> {
  if (!imageUrl || !poleImage) {
    throw new Error('Image URL and pole image data are required');
  }
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      // Set canvas to natural image size
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      
      // Draw the base image
      ctx.drawImage(img, 0, 0);
      
      // Draw reference line (yellow line) using poleImage data
      if (poleImage.referenceLine) {
        const topPoint = poleImage.referenceLine.top.pixelCoordinates;
        const bottomPoint = poleImage.referenceLine.bottom.pixelCoordinates;
        
        ctx.strokeStyle = '#FFD700'; // Yellow color
        ctx.lineWidth = 16; // Extra thick line
        ctx.beginPath();
        ctx.moveTo(topPoint.x, topPoint.y);
        ctx.lineTo(bottomPoint.x, bottomPoint.y);
        ctx.stroke();
      }
      
      // Calculate label positions with collision detection
      const attachmentData = attachments
        .map(att => {
          // Get the position for this specific image
          const imageData = att.images?.find(img => img.imageId === poleImage.id);
          if (!imageData) {
            // Fallback to position if no image-specific position
            if (!att.position) return null;
            return {
              attachment: att,
              originalY: att.position.y,
              dotX: att.position.x,
              dotY: att.position.y
            };
          }
          return {
            attachment: att,
            originalY: imageData.point.y,
            dotX: imageData.point.x,
            dotY: imageData.point.y
          };
        })
        .filter((data): data is { attachment: typeof attachments[0]; originalY: number; dotX: number; dotY: number } => data !== null)
        .sort((a, b) => a.originalY - b.originalY); // Sort top to bottom
      
      // Apply spacing logic to prevent overlaps
      const MIN_SPACING = 180; // Bigger spacing for potentially taller boxes
      const labelOffsets: Record<string, number> = {};
      let lastY = -Infinity;
      
      attachmentData.forEach(data => {
        let adjustedY = data.originalY;
        if (adjustedY < lastY + MIN_SPACING) {
          adjustedY = lastY + MIN_SPACING;
        }
        labelOffsets[data.attachment.id] = adjustedY - data.originalY;
        lastY = adjustedY;
      });
      
      // Helper function to format measurement
      const formatMeasurement = (value: number): string => {
        if (measurementUnit === 'metric') {
          return `${value.toFixed(1)}m`;
        } else if (measurementUnit === 'feet_inches') {
          const feet = Math.floor(value);
          const inches = Math.round((value - feet) * 12);
          return `${feet}'${inches}"`;
        } else {
          return `${value.toFixed(1)}ft`;
        }
      };
      
      // Draw attachments with spacing adjustments
      attachmentData.forEach(data => {
        const { attachment, dotX, dotY } = data;
        const yOffset = labelOffsets[attachment.id] || 0;
        
        // Determine attachment color based on type (same logic as canvas)
        let dotColor = '#FF0000'; // Default red
        let boxColor = '#FFFFFF'; // Default white
        let textColor = '#000000'; // Default black text
        
        if (attachment.relatedGuys && attachment.relatedGuys.length > 0) {
          // Gold color for guy attachments
          dotColor = 'rgb(255, 215, 0)';
          boxColor = 'rgb(255, 215, 0)';
          textColor = '#000000';
        } else if (attachment.type === 'wire' || (attachment.relatedSpans && attachment.relatedSpans.length > 0)) {
          // Light blue for wire/span attachments
          dotColor = 'rgb(147, 197, 253)';
          boxColor = 'rgb(147, 197, 253)';
          textColor = '#000000';
        } else if (!attachment.classification || attachment.classification === '') {
          // Red for empty classification
          dotColor = '#FF0000';
          boxColor = '#FF0000';
          textColor = '#FFFFFF';
        } else {
          // White for normal attachments
          dotColor = '#FFFFFF';
          boxColor = '#FFFFFF';
          textColor = '#000000';
        }
        
        // Draw dot on the yellow line with appropriate color
        ctx.fillStyle = dotColor;
        ctx.beginPath();
        ctx.arc(dotX, dotY, 25, 0, 2 * Math.PI); // Large dot
        ctx.fill();
        
        // Get attachment display name
        let attachmentName = '';
        const hasNoName = !attachment.inventoryClass?.name && !attachment.classification;
        
        if (attachment.inventoryClass?.name) {
          attachmentName = attachment.inventoryClass.name;
        } else if (attachment.classification) {
          attachmentName = attachment.classification;
        } else if (attachment.label) {
          attachmentName = attachment.label;
        } else {
          attachmentName = 'select name';
        }
        
        // Add guy anchor info if related
        let guyInfo = '';
        if (attachment.relatedGuys && attachment.relatedGuys.length > 0) {
          const guyIds = attachment.relatedGuys.map((id: string) => id.slice(-4)).join(', ');
          guyInfo = ` (Guy: ${guyIds})`;
        }
        
        const displayName = attachmentName + guyInfo;
        const heightText = formatMeasurement(attachment.height);
        
        // Get subcategories
        const subcategories = attachment.inventoryClass?.subcategories || [];
        const subcategoryText = subcategories
          .filter(sub => sub.value)
          .map(sub => `${sub.name}: ${sub.value}`)
          .join(' • ');
        
        // Measure text to calculate box dimensions
        ctx.font = 'bold 60px Arial';
        const nameWidth = ctx.measureText(displayName).width;
        ctx.font = 'bold 50px Arial';
        const heightWidth = ctx.measureText(heightText).width;
        
        const minTitleWidth = nameWidth + heightWidth + 80;
        let subcategoryWidth = 0;
        if (subcategoryText) {
          ctx.font = 'bold 40px Arial';
          subcategoryWidth = ctx.measureText(subcategoryText).width + 60;
        }
        
        const boxWidth = Math.max(minTitleWidth, subcategoryWidth, 500);
        const baseHeight = 110;
        const subcategoryLines = subcategoryText ? 1 : 0; // Simplified for now
        const boxHeight = baseHeight + (subcategoryLines * 50);
        const boxX = img.naturalWidth - boxWidth - 40;
        const adjustedBoxY = (dotY + yOffset) - boxHeight / 2;
        const cornerRadius = 20;
        
        // Draw rounded rectangle helper
        const drawRoundedRect = (x: number, y: number, width: number, height: number, radius: number) => {
          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + width - radius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
          ctx.lineTo(x + width, y + height - radius);
          ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
          ctx.lineTo(x + radius, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
        };
        
        // Draw background box with appropriate color
        ctx.fillStyle = boxColor;
        drawRoundedRect(boxX, adjustedBoxY, boxWidth, boxHeight, cornerRadius);
        ctx.fill();
        
        // Draw border - darker version of box color or gray
        if (boxColor === '#FFFFFF') {
          ctx.strokeStyle = '#374151';
        } else {
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        }
        ctx.lineWidth = 3;
        drawRoundedRect(boxX, adjustedBoxY, boxWidth, boxHeight, cornerRadius);
        ctx.stroke();
        
        // Draw connecting line from dot to box (use box color)
        ctx.strokeStyle = boxColor;
        ctx.lineWidth = 8;
        
        if (yOffset > 5) {
          // Curved line when offset
          const midX = dotX + (boxX - dotX) / 2;
          ctx.beginPath();
          ctx.moveTo(dotX, dotY);
          ctx.quadraticCurveTo(midX, dotY, boxX, adjustedBoxY + boxHeight / 2);
          ctx.stroke();
        } else {
          // Straight line
          ctx.beginPath();
          ctx.moveTo(dotX, dotY);
          ctx.lineTo(boxX, adjustedBoxY + boxHeight / 2);
          ctx.stroke();
        }
        
        // Draw text content with appropriate color
        ctx.fillStyle = textColor;
        
        // Height on the right
        ctx.font = 'bold 50px Arial';
        ctx.fillText(heightText, boxX + boxWidth - heightWidth - 30, adjustedBoxY + 60);
        
        // Name on the left
        ctx.font = 'bold 60px Arial';
        ctx.fillText(displayName, boxX + 30, adjustedBoxY + 60);
        
        // Subcategories (use slightly lighter version of text color)
        if (subcategoryText) {
          ctx.fillStyle = textColor === '#FFFFFF' ? '#E5E7EB' : '#6B7280';
          ctx.font = 'bold 40px Arial';
          ctx.fillText(subcategoryText, boxX + 30, adjustedBoxY + 120);
        }
      });
      
      // Draw guy anchors
      guyAnchors.forEach(guy => {
        if (guy.position) {
          const x = guy.position.x;
          const y = guy.position.y;
          
          // Draw yellow circle (matching canvas style)
          ctx.fillStyle = '#FACC15'; // yellow-400
          ctx.beginPath();
          ctx.arc(x, y, 25, 0, 2 * Math.PI);
          ctx.fill();
          
          // Draw yellow line from center to anchor
          ctx.strokeStyle = 'yellow';
          ctx.lineWidth = 12;
          ctx.beginPath();
          ctx.moveTo(img.naturalWidth / 2, img.naturalHeight / 2);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
      });
      
      resolve(canvas.toDataURL('image/png', quality));
    };
    
    img.onerror = reject;
    img.src = imageUrl;
  });
}